import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api/client";

/** A lender tenant and its vital signs (null when its data could not be read). */
export interface TenantSummary {
  tenantId: string;
  slug: string;
  displayName: string;
  tier: "EXCEL" | "GRAVITON";
  status: "ACTIVE" | "FROZEN" | "PROVISIONING" | "OFFBOARDED";
  schemaName: string;
  createdAt: string;
  users: number | null;
  activeUsers30d: number | null;
  lastSignInAt: string | null;
  openCases: number | null;
  latestDuesFile: string | null;
}

export interface NewTenant {
  slug: string;
  displayName: string;
  tier: "EXCEL" | "GRAVITON";
  adminEmpId: string;
  adminName: string;
  adminEmail: string;
  adminMobile: string;
  adminTempPassword: string;
}

const KEY = ["platform", "tenants"];

export function usePlatformTenants() {
  return useQuery({
    queryKey: KEY,
    queryFn: async () =>
      (await api.get<TenantSummary[]>("/v1/platform/tenants")).data,
    refetchInterval: 60_000,
  });
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: NewTenant) =>
      (await api.post<TenantSummary>("/v1/platform/tenants", body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useSetTenantEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (p: { slug: string; enable: boolean; reason?: string }) =>
      (
        await api.post<TenantSummary>(
          `/v1/platform/tenants/${p.slug}/${p.enable ? "enable" : "disable"}`,
          p.enable ? undefined : { reason: p.reason },
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** The server's reason leads with a code ("slug_taken: …"); show the sentence. */
export function errorText(err: unknown, fallback: string): string {
  const data = (
    err as { response?: { data?: { detail?: string; message?: string } } }
  )?.response?.data;
  return String(data?.detail ?? data?.message ?? fallback).replace(
    /^[a-z_]+:\s*/,
    "",
  );
}

// ---------------------------------------------------------------------------
// Dashboard — aggregates only; money arrives as JSON numbers.

export interface PlatformDay {
  date: string;
  activeUsers: number;
  webLogins: number;
  mobileLogins: number;
  cashCollected: number;
  digitalCollected: number;
  dispositions: number;
  visits: number;
  ptps: number;
}

export interface TenantMetrics {
  slug: string;
  displayName: string;
  tier: "EXCEL" | "GRAVITON";
  status: TenantSummary["status"];
  createdAt: string;
  /** False when its data could not be read; every block below is then null. */
  readable: boolean;
  adoption: {
    users: number;
    activeToday: number;
    active7d: number;
    active30d: number;
    neverSignedIn: number;
    totpEnrolled: number;
    webUsers: number;
    mobileUsers: number;
    newUsers: number;
    appBuilds: Record<string, number>;
  } | null;
  business: {
    openCases: number;
    outstanding: number;
    averageDpd: number | null;
    cycleCases: number;
    cycleDue: number;
    cyclePaidCases: number;
    cyclePaidAmount: number;
    cashReceipts: number;
    cashCollected: number;
    digitalPayments: number;
    digitalCollected: number;
    ptpsKept: number;
    ptpsResolved: number;
    loans: number;
    newLoans: number;
  } | null;
  /** Feature key → count in the window, in display order. */
  features: Record<string, number> | null;
  health: {
    duesAsOf: string | null;
    duesAgeDays: number | null;
    failedUploads: number;
    deadNotifications: number;
    failedPayments: number;
    agentsHoldingCash: number;
    cashInHand: number;
    agentsDepositOverdue: number;
    lotsAwaitingConfirmation: number;
    openReconFindings: number;
    schemaVersion: number | null;
  } | null;
  daily: PlatformDay[];
}

export interface PlatformDashboard {
  generatedAt: string;
  days: number;
  from: string;
  to: string;
  tenants: TenantMetrics[];
}

export function usePlatformDashboard(days: number) {
  return useQuery({
    queryKey: ["platform", "dashboard", days],
    queryFn: async () =>
      (
        await api.get<PlatformDashboard>("/v1/platform/dashboard", {
          params: { days },
        })
      ).data,
    refetchInterval: 5 * 60_000,
  });
}
